<template>
    <transition name="modal">
        <div class="modal-mask quote-send-modal-mask">
            <div class="modal-wrapper">
                <div class="modal-container">
                    <div class="modal-header">
                        <i class="close icon" v-on:click="$emit('close')"></i>
                        <h3>Send {{ capitalizeFirstLetter(model) }}</h3>
                    </div>

                    <div class="ui divider"></div>

                    <div class="modal-body scrolling content">
                        <form class="ui form">
                            <div class="field">
                                <app-field-label>To:</app-field-label>
                                <input v-model="to" type="text" placeholder="e.g. email@example.com">
                            </div>

                            <div class="field">
                                <app-field-label>Subject:</app-field-label>
                                <input v-model="subject" type="text">
                            </div>

                            <div class="field">
                                <app-field-label>Message:</app-field-label>
                                <textarea v-model="message"></textarea>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer send-email-footer">
                        <div class="send-email-footer__attachments-label">Attachments ({{ attachmentCards.length }}):</div>
                        <div class="send-email-footer__row">
                            <button
                                type="button"
                                class="send-email-footer__attach-btn"
                                @click="onAttachClick"
                            >
                                <span class="send-email-footer__attach-plus" aria-hidden="true">+</span>
                                Attach
                            </button>
                            <app-attachment-card
                                v-for="card in attachmentCards"
                                :key="card.id"
                                :title="card.filename"
                                :subtitle="card.sizeLabel"
                                :thumbnail="card.thumbnail || ''"
                                @remove="confirmRemoveAttachment(card)"
                            ></app-attachment-card>
                        </div>
                        <div class="send-email-footer__actions">
                            <div class="ui basic button smardie-btn-secondary" v-on:click="$emit('close')">Cancel</div>
                            <div
                                class="ui primary button"
                                v-on:click="send"
                                v-bind:class="{loading:sending, disabled:sending || !canSend}"
                            >Send</div>
                        </div>
                    </div>

                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        style="position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; z-index: -1;"
                        @change="onFilesSelected($event.target.files)"
                    >
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import {clone} from "../helpers/clone";
import axios from "axios";
import {capitalizeFirstLetter} from "../helpers/capitalizeFirstLetter";
import {
    processUserFile,
    wouldExceedTotal,
    sumAttachmentBytes,
    MAX_TOTAL_ATTACHMENT_BYTES,
    formatFileSize
} from "../helpers/emailAttachments";

export default {
    computed: {
        display () {
            const fromStore = this.$store.getters['setting/getDisplay'] || {}
            if (fromStore.business_name || fromStore.email) return fromStore
            return this.localDisplay || fromStore || {}
        },
        model () {
            const forced = String(this.documentType || '').toLowerCase();
            if (forced === 'invoice' || forced === 'quote') return forced;
            return this.item.number ? this.item.number.startsWith('INV') ? 'invoice' : 'quote' : '';
        },

        isQuote () {
            return this.model === 'quote';
        },

        isInvoice () {
            return this.model === 'invoice';
        },

        attachmentCards () {
            const docs = this.includeDocument && this.documentAttachment
                ? [this.documentAttachment]
                : []
            return docs.concat(this.userAttachments || [])
        },

        canSend () {
            return this.attachmentCards.length > 0
        }
    },

    data: () => ({
        item: {},
        message: null,
        sending: false,
        subject: null,
        templates: {},
        to: null,
        localDisplay: {},
        includeDocument: true,
        documentAttachment: null,
        userAttachments: [],
        attaching: false
    }),

    methods: {
        capitalizeFirstLetter,

        syncItemFromContent () {
            try {
                const c = this.content;
                this.item = (c && typeof c === 'object' && !Array.isArray(c)) ? clone(c) : {};
            } catch (e) {
                this.item = {};
            }
            this.initDocumentAttachment()
        },

        initDocumentAttachment () {
            const number = (this.item && this.item.number) ? this.item.number : 'document'
            this.documentAttachment = {
                id: 'doc-main',
                kind: 'document',
                filename: number + '.pdf',
                sizeLabel: 'PDF',
                thumbnail: '',
                size: 0
            }
            this.includeDocument = true
        },

        onAttachClick () {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
                this.$refs.fileInput.click()
            }
        },

        confirmRemoveAttachment (card) {
            if (!card) return
            this.swal({
                title: 'Are you sure?',
                text: 'Remove ' + (card.filename || 'this attachment') + ' from the email?',
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                confirmButtonText: 'Yes, remove it'
            }).then((result) => {
                if (result && result.value) {
                    this.removeAttachment(card)
                }
            })
        },

        removeAttachment (card) {
            if (!card) return
            if (card.kind === 'document') {
                this.includeDocument = false
            } else {
                this.userAttachments = (this.userAttachments || []).filter((u) => u.id !== card.id)
            }
        },

        async onFilesSelected (files) {
            if (!files || !files.length) return
            this.attaching = true
            try {
                for (let i = 0; i < files.length; i++) {
                    const file = files[i]
                    let processed
                    try {
                        processed = await processUserFile(file)
                    } catch (err) {
                        this.errorNotification((err && err.message) || 'Could not add file')
                        continue
                    }
                    const existing = this.userAttachments.concat(
                        this.includeDocument ? [{ size: 0 }] : []
                    )
                    if (wouldExceedTotal(existing, processed.size)) {
                        this.errorNotification(
                            'Attachments are too large to send. Total must be under ' +
                            formatFileSize(MAX_TOTAL_ATTACHMENT_BYTES) + '.'
                        )
                        break
                    }
                    this.userAttachments = this.userAttachments.concat([processed])
                }
            } finally {
                this.attaching = false
            }
        },

        getTemplates: function () {
            axios.get('/api/email/template').then(response => {
                this.templates = (response.data && typeof response.data === 'object') ? response.data : {};
                this.buildEmailMessage();
            }).catch(() => {
                this.templates = { quote_message: '', invoice_message: '' };
                this.buildEmailMessage();
            });
        },

        send: function() {
            const raw = (this.to && String(this.to).trim()) || '';
            const addresses = raw.split(',').map(function (e) { return e.trim(); }).filter(Boolean);
            if (addresses.length === 0) {
                this.errorNotification('Please enter at least one recipient email address');
                return;
            }
            const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const invalid = addresses.filter(function (e) { return !emailRe.test(e); });
            if (invalid.length > 0) {
                this.errorNotification('Invalid email address: ' + invalid[0]);
                return;
            }
            if (!this.canSend) {
                this.errorNotification('Add at least one attachment to send');
                return;
            }
            const userBytes = sumAttachmentBytes(this.userAttachments);
            if (userBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
                this.errorNotification(
                    'Attachments are too large to send. Total must be under ' +
                    formatFileSize(MAX_TOTAL_ATTACHMENT_BYTES) + '.'
                );
                return;
            }
            this.sending = true;
            const base = this.model === 'invoice' ? '/invoice' : '/quote';
            const id = this.item.uuid || this.item.id;
            const extra_attachments = (this.userAttachments || []).map((u) => ({
                filename: u.filename,
                content: u.content,
                type: u.type,
                size: u.size
            }));
            const payload = {
                to: this.to,
                subject: this.subject,
                message: this.message,
                include_pdf: !!this.includeDocument,
                extra_attachments: extra_attachments
            };
            axios.post(`${base}/${id}/send`, payload).then(response => {
                this.sending = false;
                let text = `Your ${this.model} has been sent successfully`;
                const xs = response.data && response.data.xero_sync;
                if (this.isInvoice && xs && xs.synced === false) {
                    text += '\n\nXero did not sync this invoice: ' + (xs.reason || 'Unknown error');
                } else if (this.isInvoice && xs && xs.synced === true && xs.xero_status === 'DRAFT') {
                    text += '\n\nSaved in Xero as a Draft (set Sales account under Settings → Integrations to post as Authorised).';
                }
                this.swal({
                    type: 'success',
                    title: `${capitalizeFirstLetter(this.model)} Sent`,
                    text: text
                }).then(result => {
                    this.$emit('sent');
                })
            }).catch(error => {
                this.sending = false;
                const msg = (error.response && error.response.data && error.response.data.error)
                    ? String(error.response.data.error)
                    : 'Failed to send email. Please try again.';
                if (this.errorNotification) this.errorNotification(msg);
            });
        },

        /** Same idea as quote send modal: person name, else contact.name, else company (skip placeholder "—"). */
        resolveCustomerSubjectLabel (contact) {
            const c = contact || {};
            const firstLast = [c.first_name, c.last_name].filter(function (x) { return x != null && String(x).trim() !== ''; }).join(' ').trim();
            if (firstLast) return firstLast;
            const n = c.name != null ? String(c.name).trim() : '';
            if (n && n !== '—') return n;
            const comp = c.company != null ? String(c.company).trim() : '';
            if (comp) return comp;
            return '';
        },

        /** Match quote send modal: nested contact.email, then flat contact_email on the document. */
        resolveContact () {
            const item = this.item || {};
            const c = item.contact || {};
            const first_name = c.first_name || item.contact_first_name || '';
            const last_name = c.last_name || item.contact_last_name || '';
            const company = c.company || item.contact_company || '';
            const name = c.name || item.contact_name || '';
            const email = String(c.email || item.contact_email || '').trim();
            return { first_name, last_name, company, name, email };
        },

        applyRecipientFields (contact) {
            const c = contact || this.resolveContact();
            this.to = c.email || '';
            const prefix = this.model === 'invoice' ? 'Invoice' : 'Quote';
            const num = this.item.number || '';
            const label = this.resolveCustomerSubjectLabel(c);
            this.subject = (prefix + ' ' + num + (label ? ' – ' + label : '')).trim();
        },

        /**
         * Invoice list rows sometimes omit a usable contact.email; quote send loads full quote.
         * Fall back to GET /quote/:id so To is prefilled the same way.
         */
        ensureRecipientEmail () {
            const existing = this.resolveContact();
            if (existing.email) {
                this.applyRecipientFields(existing);
                return;
            }
            const id = this.item.uuid || this.item.id;
            if (id == null || id === '') {
                this.applyRecipientFields(existing);
                return;
            }
            axios.get('/quote/' + id).then((response) => {
                const q = (response.data && (response.data.quote || response.data)) || {};
                const nested = q.contact || {};
                const email = String(nested.email || q.contact_email || '').trim();
                const contact = {
                    email: email,
                    first_name: nested.first_name || q.contact_first_name || existing.first_name || '',
                    last_name: nested.last_name || q.contact_last_name || existing.last_name || '',
                    company: nested.company || q.contact_company || existing.company || '',
                    name: nested.name || q.contact_name || existing.name || ''
                };
                if (!this.item.contact || typeof this.item.contact !== 'object') {
                    this.item.contact = {};
                }
                Object.assign(this.item.contact, contact);
                if (email) this.item.contact_email = email;
                this.applyRecipientFields(contact);
                this.buildEmailMessage();
            }).catch(() => {
                this.applyRecipientFields(existing);
            });
        },

        buildEmailMessage () {
            const contact = this.resolveContact();
            const disp = this.display;
            let msg = this.isQuote ? (this.templates.quote_message || '') : (this.templates.invoice_message || '');
            msg = msg.replace(/\[Customer First Name\]/g, contact.first_name || contact.name || contact.company || '');
            msg = msg.replace(/\[Quote Number\]/g, this.item.number || '');
            msg = msg.replace(/\[Number\]/g, this.item.number || '');
            msg = msg.replace(/\[Quote Total Amount\]/g, '$' + (this.item.total != null ? Number(this.item.total).toFixed(2) : '0.00'));
            msg = msg.replace(/\[Total Amount\]/g, '$' + (this.item.total != null ? Number(this.item.total).toFixed(2) : '0.00'));
            msg = msg.replace(/\[Company Name\]/g, disp.business_name || '');
            msg = msg.replace(/\[Terms\]/g, this.item.terms != null ? this.item.terms : '');
            this.message = msg;
        },
        ensureDisplayLoaded () {
            const existing = this.$store.getters['setting/getDisplay'] || {}
            if (existing.business_name || existing.email) {
                this.getTemplates()
                return
            }
            axios.get('/api/setting/display').then((response) => {
                const data = (response && response.data) ? response.data : {}
                this.$store.commit('setting/setDisplay', data)
                this.localDisplay = data
                this.getTemplates()
            }).catch(() => {
                this.localDisplay = {}
                this.getTemplates()
            })
        }
    },

    mounted() {
        this.syncItemFromContent();
        this.ensureRecipientEmail();
        this.ensureDisplayLoaded();
    },

    props: {
        content: { type: Object },
        /** Optional override so invoice send does not depend on INV- number prefix detection. */
        documentType: {
            type: String,
            default: ''
        }
    },

    watch: {
        content: {
            handler() {
                this.syncItemFromContent();
                this.ensureRecipientEmail();
            },
            deep: true
        }
    }
}
</script>

<style scoped>

</style>
